/* =========================================================================
   TrustScore sorting for collection pages (client-side).

   Shopify's native sort_by can't sort by a metafield (and Search & Discovery
   only adds filters, never sort options), so `?sort_by=trust-score` is a sort
   Shopify ignores — it renders the collection's default order — and the
   results list takes over on the client. That happens INSIDE
   assets/paginated-list.js (`PaginatedList.clientSorts`): it loads every page
   of the (filtered) collection once through the Section Rendering API, orders
   the cards by their data-trust-score (desc; unscored cards last — see
   snippets/product-card.liquid), and then pages through the ordered set with
   the same infinite scroll, page windowing and scroll handling as any server
   sort. Doing it there rather than re-ordering the live grid from outside is
   what keeps the grid from reshuffling under the shopper once the pages are
   in, and keeps a 900-card catalogue windowed instead of fully in the DOM.

   The list follows the theme's own lifecycle for it: a filter or sort change
   morphs the section in place (assets/facets.js) and the list re-reads the
   URL; a sidebar category pick swaps the whole section (collection-nav.js)
   and the new results-list reads the URL on connect; back/forward goes
   through the same two paths.

   This file only does what Liquid cannot, because it never sees the query
   string:
     1. lets selecting "TrustScore" go through the theme's facets flow
        (pushState + section morph) like every other sort — the list picks it
        up from the morph — and navigates only on a template with no facets
        form to do that;
     2. keeps the sort control marked "TrustScore" while the URL says so, after
        every render that would reset it to the server's default;
     3. reports the pick to GTM (`trust_score_sort`, assets/tracking.js).
   ========================================================================= */
(function () {
  var SORT = 'trust-score';
  var LIST = 'results-list';

  function currentSort() {
    return new URLSearchParams(window.location.search).get('sort_by');
  }

  function isActive() {
    return currentSort() === SORT;
  }

  // ── selecting the option ─────────────────────────────────────────────────
  // The sorting web component submits the facets form (pushState + morph) for
  // any radio/select value, our fake one included, so normally we let it run
  // and results-list picks the change up from the morph. Only when there is
  // no facets form to do that (a template that renders the sort on its own)
  // do we navigate.
  document.addEventListener(
    'change',
    function (e) {
      var t = e.target;
      if (!t || t.name !== 'sort_by') return;
      // Before either path below: the facets one morphs in place, but the
      // navigating one leaves this tick with the page, and only a push that
      // went through the queue survives that. Programmatic re-marking in
      // syncUI never raises `change`, so this is one push per pick.
      if (t.value === SORT) track({ event: 'trust_score_sort' });
      var section = t.closest('.shopify-section');
      var hasFacets = !!(section && section.querySelector('facets-form-component'));
      if (hasFacets) return;
      if (t.value !== SORT && !isActive()) return;
      e.stopImmediatePropagation();
      e.preventDefault();
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', t.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    },
    true
  );

  // ── sort control ─────────────────────────────────────────────────────────
  var scheduleSync = debounce(function () {
    syncUI(isActive());
  }, 120);

  ready(function () {
    syncUI(isActive());
    // The facets form re-rendered the section (filter or sort change).
    document.addEventListener('filter:rendered', scheduleSync);
    // collection-nav.js replaces the whole section when a sidebar category is
    // picked, so the sort control we marked is gone with it.
    if ('MutationObserver' in window) {
      var lastList = document.querySelector(LIST);
      new MutationObserver(function () {
        var list = document.querySelector(LIST);
        if (list === lastList) return;
        lastList = list;
        scheduleSync();
      }).observe(document.body, { childList: true, subtree: true });
    }
    // Back/forward between a filtered and an unfiltered trust URL.
    window.addEventListener('popstate', scheduleSync);
  });

  // The server never knows about this sort, so after every render the control
  // says "Best Selling" (the default). Mark our option — and only within the
  // sorting component: each filter accordion has its own .facets__status.
  function syncUI(active) {
    document.querySelectorAll('sorting-filter-component').forEach(function (comp) {
      var label = '';
      comp.querySelectorAll('select[name="sort_by"]').forEach(function (sel) {
        var has = Array.prototype.some.call(sel.options, function (o) {
          return o.value === SORT;
        });
        if (has && active) sel.value = SORT;
      });
      comp.querySelectorAll('input[name="sort_by"]').forEach(function (r) {
        var mine = r.value === SORT;
        if (active) r.checked = mine;
        var opt = r.closest('[role="option"]');
        if (opt) {
          var selected = active ? mine : r.checked;
          opt.setAttribute('aria-selected', selected ? 'true' : 'false');
          opt.setAttribute('tabindex', selected ? '0' : '-1');
        }
        if (mine && r.checked && active) label = r.getAttribute('data-option-name') || 'TrustScore';
      });
      if (!active) return;
      comp.querySelectorAll('.facets__status').forEach(function (s) {
        s.textContent = label || 'TrustScore';
      });
    });
  }

  // ── utils ────────────────────────────────────────────────────────────────
  // assets/tracking.js is a module and this is a classic script: both are
  // deferred and it comes first in the document, but a click can still land
  // before it has evaluated, and a bare push is better than a dropped one.
  function track(payload) {
    if (window.BirchTracking) window.BirchTracking.push(payload);
    else (window.dataLayer = window.dataLayer || []).push(payload);
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  }
})();
